// @flow

import React from 'react';
import DocumentTitle from 'react-document-title';
import {
  Box,
  Card,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { useCertificationApprovals } from './hooks';
import CertificationFunnel from './CertificationFunnel';
import CertDrillDownPanel from './CertDrillDownPanel';

const NAME = 'certification-approvals';

const CertificationApprovals = () => {
  const {
    documentTitle,
    header,
    groups,
    holdStages,
    selectedStageKey,
    selectedStageLabel,
    onSelectStage,
    organizationId,
    propertyId,
    isLoading,
    isError,
    errorMessage,
    isEmpty,
    emptyMessage,
  } = useCertificationApprovals();

  return (
    <DocumentTitle title={documentTitle}>
      <Box data-testid={NAME} sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Typography variant="titleLarge">{header}</Typography>
          {/* Funnel on the left, drill-down panel on the right; they stack on
              narrow screens. AsyncBox owns the funnel's loading and error
              states (the shared component, not a hand-rolled spinner); the
              empty state is distinct — a valid response with no open certs is
              not an error, so it renders its own plain message rather than a
              funnel of zero bars.

              The drill-down panel renders only once the funnel has data: it
              needs a selected stage (from the stages data) and the property to
              fetch its certs, so there is nothing for it to show while the
              funnel is loading, has errored, or the property has no open certs
              at all. The panel owns its own loading / error / empty states
              internally, so a failure loading one stage's certs never disturbs
              the funnel beside it. The selected stage — set from the default in
              the stages response on load, and by any funnel click after
              (including the hold row) — drives which certs it shows. */}
          <Box
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
            }}
          >
            {/* The funnel fills the width left over beside the panel (flex: 1).
                It sits in a Card — the app's standard surface — with the panel
                (see CertDrillDownPanel) in its own Card beside it. minWidth: 0
                lets this side shrink on a narrow viewport. */}
            <Card
              sx={{
                flex: 1,
                minWidth: 0,
                p: 2.5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              }}
            >
              {/* Error is rendered explicitly, not delegated to AsyncBox, so a
                  failed stages/counts request surfaces a message instead of
                  falling through to a blank surface. Loading and the loaded
                  funnel stay inside AsyncBox, driven by its `loading` prop. */}
              {isError ? (
                <Typography
                  role="alert"
                  variant="bodyMedium"
                  sx={{ color: 'error.main' }}
                  data-testid="certification-funnel-error"
                >
                  {errorMessage}
                </Typography>
              ) : (
                <AsyncBox loading={isLoading}>
                  {isEmpty ? (
                    <Typography
                      variant="bodyMedium"
                      data-testid="certification-funnel-empty"
                    >
                      {emptyMessage}
                    </Typography>
                  ) : (
                    <CertificationFunnel
                      groups={groups}
                      holdStages={holdStages}
                      selectedStageKey={selectedStageKey}
                      onSelectStage={onSelectStage}
                    />
                  )}
                </AsyncBox>
              )}
            </Card>
            {!isLoading && !isError && !isEmpty && (
              <CertDrillDownPanel
                organizationId={organizationId}
                propertyId={propertyId}
                stageKey={selectedStageKey}
                stageLabel={selectedStageLabel}
              />
            )}
          </Box>
        </Stack>
      </Box>
    </DocumentTitle>
  );
};

export default CertificationApprovals;
